import {
  Link as BaseLink,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_GlobalFilterTextField as SearchField,
  useMaterialReactTable,
} from 'material-react-table';
import { Link } from 'react-router-dom';
import { formatDate, getMaterialColor } from '../../helpers';
import { ColorDot } from '../design-system';
import { FindBatchActionsMenu } from './FindBatchActionsMenu';
import { FindListFilters } from './FindListFilters';
import { searchFieldStyles } from './searchFieldStyles';
import { FindResult, FindResultsState } from './types';
import {
  EMPTY_FIND_FILTER_LIST_KEYS,
  FindFilterListKey,
} from '../FindToolbox/Filters/types';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMaterialReactTableLocalization, useVocabulary } from '../../i18n';

const gridStyles = {
  marginBottom: 6,
  display: 'grid',
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 'none',
  backgroundImage: 'none',
  overflow: 'hidden',
  '& .MuiTableRow-root.Mui-selected': {
    bgcolor: 'rgba(88, 101, 242, 0.09)',
  },
  '& .MuiTableRow-root.Mui-selected:hover': {
    bgcolor: 'rgba(88, 101, 242, 0.13)',
  },
  '& .MuiTableCell-root': {
    borderColor: 'divider',
  },
};
const toolbarStyles = {
  display: 'flex',
  gap: 1.5,
  px: 2,
  py: 1.5,
  alignItems: 'center',
  flexWrap: 'wrap',
  borderBottom: '1px solid',
  borderColor: 'divider',
};
const createButtonStyles = { minHeight: 38 };
const selectionStyles = {
  ml: { xs: 0, md: 1.5 },
  pl: { md: 2 },
  borderLeft: { md: '1px solid' },
  borderColor: { md: 'divider' },
  alignItems: 'center',
};
const selectionTextStyles = { whiteSpace: 'nowrap' };
const selectionCountStyles = { fontWeight: 500 };
const materialCellStyles = { alignItems: 'center' };
const countStyles = {
  ml: 'auto',
  color: 'text.secondary',
  whiteSpace: 'nowrap',
};
const PAGE_SIZE = 50;

interface FindGridProps {
  rows: FindResult[];
  state: FindResultsState;
  loading?: boolean;
  createHref?: string | null;
  enableSiteFilter?: boolean;
  enableGroupFilter?: boolean;
  showCopyFromMap?: boolean;
  presetOmittedKeys?: readonly FindFilterListKey[];
}

export function FindGrid({
  rows,
  state,
  loading = false,
  createHref = '/f/create',
  enableSiteFilter = true,
  enableGroupFilter = true,
  showCopyFromMap = true,
  presetOmittedKeys = EMPTY_FIND_FILTER_LIST_KEYS,
}: FindGridProps) {
  const intl = useIntl();
  const vocabulary = useVocabulary();
  const localization = useMaterialReactTableLocalization();
  const columns = useMemo<MRT_ColumnDef<FindResult>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 100,
        Cell: ({ renderedCellValue, cell }) => (
          <BaseLink
            underline="none"
            component={Link}
            to={`/f/${cell.getValue()}/edit`}
          >
            <span>{renderedCellValue}</span>
          </BaseLink>
        ),
      },
      { accessorKey: 'evId', header: 'EvID', size: 50 },
      {
        id: 'type',
        accessorFn: (row) => vocabulary('findType', row.type),
        header: intl.formatMessage({
          id: 'find.field.type',
          defaultMessage: 'Find type',
        }),
        size: 180,
      },
      {
        id: 'material',
        accessorFn: (row) => vocabulary('material', row.material),
        header: intl.formatMessage({
          id: 'find.field.material',
          defaultMessage: 'Material',
        }),
        size: 120,
        filterVariant: 'multi-select',
        Cell: ({ renderedCellValue, row }) => {
          const [red, green, blue] = getMaterialColor(
            row.original.material || 'unknown'
          );

          return (
            <Stack direction="row" gap={1} sx={materialCellStyles}>
              <ColorDot color={`rgb(${red}, ${green}, ${blue})`} />
              <span>{renderedCellValue}</span>
            </Stack>
          );
        },
      },
      {
        id: 'era',
        accessorFn: (row) => vocabulary('era', row.era),
        header: intl.formatMessage({
          id: 'find.field.era',
          defaultMessage: 'Era',
        }),
        size: 180,
      },
      {
        accessorKey: 'description',
        header: intl.formatMessage({
          id: 'form.description',
          defaultMessage: 'Description',
        }),
        size: 220,
      },
      {
        Cell: ({ cell }) => formatDate(cell.getValue<Date | null>()) || '-',
        accessorFn: (row) => row.foundAt && new Date(row.foundAt),
        header: intl.formatMessage({
          id: 'find.field.dateFound',
          defaultMessage: 'Date found',
        }),
        size: 120,
      },
    ],
    [intl, vocabulary]
  );
  const { search, setSearch, page, setPage } = state;
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pagination = {
    pageIndex: Math.min(page, pageCount) - 1,
    pageSize: PAGE_SIZE,
  };

  const table = useMaterialReactTable({
    columns,
    data: rows,
    localization,
    enableRowSelection: true,
    enableToolbarInternalActions: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    initialState: { density: 'compact', showGlobalFilter: true },
    state: { isLoading: loading, globalFilter: search, pagination },
    autoResetPageIndex: false,
    onGlobalFilterChange: setSearch,
    onPaginationChange: (updater) => {
      const next =
        typeof updater === 'function' ? updater(pagination) : updater;
      setPage(next.pageIndex + 1);
    },
    muiPaginationProps: { showRowsPerPage: false },
    muiSearchTextFieldProps: ({ table: currentTable }) => ({
      size: 'small',
      sx: searchFieldStyles,
      InputProps: {
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon onClick={() => currentTable.resetGlobalFilter()} />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            {!!currentTable.getState().globalFilter ? (
              <ClearIcon
                cursor="pointer"
                onClick={() => currentTable.resetGlobalFilter()}
              />
            ) : (
              <ClearIcon htmlColor="transparent" />
            )}
          </InputAdornment>
        ),
      },
    }),
    muiTablePaperProps: { sx: gridStyles },
    renderTopToolbar: ({ table: currentTable }) => {
      const selectedRows = currentTable.getSelectedRowModel().rows;
      const selectedFindIds = selectedRows.map((row) => row.original.id);

      return (
        <Box sx={toolbarStyles}>
          {createHref && (
            <Button
              component={Link}
              to={createHref}
              color="primary"
              variant="contained"
              startIcon={<AddIcon />}
              sx={createButtonStyles}
            >
              <FormattedMessage
                id="find.action.create"
                defaultMessage="Create find"
              />
            </Button>
          )}
          <SearchField table={currentTable} />
          <FindListFilters
            state={state}
            enableSiteFilter={enableSiteFilter}
            enableGroupFilter={enableGroupFilter}
            showCopyFromMap={showCopyFromMap}
            presetOmittedKeys={presetOmittedKeys}
          />
          {selectedRows.length > 0 && (
            <Stack direction="row" gap={1} sx={selectionStyles}>
              <Typography variant="body2" sx={selectionTextStyles}>
                <FormattedMessage
                  id="finds.selection.count"
                  defaultMessage="{count} selected"
                  values={{
                    count: (
                      <Box component="span" sx={selectionCountStyles}>
                        {selectedRows.length}
                      </Box>
                    ),
                  }}
                />
              </Typography>
              <FindBatchActionsMenu
                findIds={selectedFindIds}
                onDeleted={currentTable.resetRowSelection}
              />
              <IconButton
                size="small"
                aria-label={intl.formatMessage({
                  id: 'finds.selection.clear',
                  defaultMessage: 'Clear selection',
                })}
                onClick={() => currentTable.resetRowSelection()}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}
          <Typography variant="body2" sx={countStyles}>
            <FormattedMessage
              id="finds.count"
              defaultMessage="{count, plural, one {# find} other {# finds}}"
              values={{ count: rows.length }}
            />
          </Typography>
        </Box>
      );
    },
  });

  return <MaterialReactTable table={table} />;
}
